import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { SectionHeader } from "@/components/common/Widgets";
import { fmtDate } from "@/lib/format";
import { Plus, Trash2, Database } from "lucide-react";

export default function MasterDataPage() {
  return (
    <div className="space-y-3">
      <SectionHeader title="Banks, Branches & Configurations" subtitle="Master data setup. Only HO can manage." />
      <Tabs defaultValue="branches">
        <TabsList className="bg-muted/60">
          <TabsTrigger value="banks" data-testid="master-tab-banks">Banks</TabsTrigger>
          <TabsTrigger value="branches" data-testid="master-tab-branches">Branches</TabsTrigger>
          <TabsTrigger value="configs" data-testid="master-tab-configs">Configurations</TabsTrigger>
        </TabsList>
        <TabsContent value="banks"><Banks /></TabsContent>
        <TabsContent value="branches"><Branches /></TabsContent>
        <TabsContent value="configs"><Configs /></TabsContent>
      </Tabs>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (<div className={`space-y-1.5 ${className}`}><Label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">{label}</Label>{children}</div>);
}

function Banks() {
  const { user } = useAuth();
  const canManage = user?.role === "HO";
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", short_name: "", head_office_address: "", ifsc_prefix: "", regions: "East,West", product_catalog: "CC,TL,PERSONAL_LOAN,HOUSING_LOAN", contact_email: "", contact_phone: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => { try { const r = await api.get("/master/banks"); setItems(r.data); } catch (e) {} };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const add = async () => {
    if (!form.name) return toast.error("Bank name is required");
    setBusy(true);
    try {
      await api.post("/master/banks", { ...form, regions: form.regions.split(",").map(s => s.trim()).filter(Boolean), product_catalog: form.product_catalog.split(",").map(s => s.trim()).filter(Boolean) });
      toast.success("Bank added");
      setForm({ name: "", short_name: "", head_office_address: "", ifsc_prefix: "", regions: "East,West", product_catalog: "CC,TL,PERSONAL_LOAN,HOUSING_LOAN", contact_email: "", contact_phone: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this bank?")) return;
    try { await api.delete(`/master/banks/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error("Failed"); }
  };
  return (
    <div className="space-y-3 mt-3">
      {canManage && (
        <Card className="p-4">
          <div className="text-sm font-semibold heading mb-3 flex items-center gap-2"><Plus className="h-4 w-4 text-accent" />Add Bank</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Field label="Name"><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="bank-name-input" /></Field>
            <Field label="Short Name"><Input value={form.short_name} onChange={(e) => set("short_name", e.target.value)} /></Field>
            <Field label="IFSC Prefix"><Input value={form.ifsc_prefix} onChange={(e) => set("ifsc_prefix", e.target.value)} /></Field>
            <Field label="Contact Email"><Input value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} /></Field>
            <Field label="Regions (comma-separated)" className="md:col-span-2"><Input value={form.regions} onChange={(e) => set("regions", e.target.value)} /></Field>
            <Field label="Product Catalog" className="md:col-span-2"><Input value={form.product_catalog} onChange={(e) => set("product_catalog", e.target.value)} /></Field>
            <Field label="Head Office Address" className="md:col-span-4"><Textarea rows={2} value={form.head_office_address} onChange={(e) => set("head_office_address", e.target.value)} /></Field>
            <div className="md:col-span-4 flex justify-end"><Button onClick={add} disabled={busy} data-testid="bank-add-button">Add Bank</Button></div>
          </div>
        </Card>
      )}
      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground"><tr><th className="text-left px-3 py-2.5">Name</th><th className="text-left px-3 py-2.5">Short</th><th className="text-left px-3 py-2.5">IFSC Prefix</th><th className="text-left px-3 py-2.5">Regions</th><th className="text-left px-3 py-2.5">Products</th><th></th></tr></thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={6} className="text-center py-12 text-muted-foreground"><Database className="h-7 w-7 mx-auto mb-2" />No banks yet.</td></tr>}
              {items.map(b => (
                <tr key={b.id} className="border-t border-border"><td className="px-3 py-2 font-medium">{b.name}</td><td className="px-3 py-2">{b.short_name}</td><td className="px-3 py-2 mono text-xs">{b.ifsc_prefix}</td><td className="px-3 py-2 text-xs">{(b.regions || []).join(", ")}</td><td className="px-3 py-2 text-xs">{(b.product_catalog || []).length} products</td><td className="px-3 py-2 text-right">{canManage && <Button size="sm" variant="outline" onClick={() => remove(b.id)}><Trash2 className="h-3.5 w-3.5" /></Button>}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function Branches() {
  const { user } = useAuth();
  const canManage = user?.role === "HO" || user?.role === "RO";
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ code: "", name: "", ifsc: "", address: "", region: "East", branch_type: "Retail", state: "", district: "", pin: "", contact_phone: "", contact_email: "", is_active: true });
  const [busy, setBusy] = useState(false);

  const load = async () => { const r = await api.get("/master/branches"); setItems(r.data); };
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const add = async () => {
    if (!form.code || !form.name) return toast.error("Code & Name required");
    setBusy(true);
    try { await api.post("/master/branches", form); toast.success("Branch added"); setForm({ code: "", name: "", ifsc: "", address: "", region: "East", branch_type: "Retail", state: "", district: "", pin: "", contact_phone: "", contact_email: "", is_active: true }); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this branch?")) return; await api.delete(`/master/branches/${id}`); toast.success("Deleted"); load(); };
  return (
    <div className="space-y-3 mt-3">
      {canManage && (
        <Card className="p-4">
          <div className="text-sm font-semibold heading mb-3 flex items-center gap-2"><Plus className="h-4 w-4 text-accent" />Add Branch</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Field label="Code"><Input value={form.code} onChange={(e) => set("code", e.target.value)} data-testid="branch-code-input" /></Field>
            <Field label="Name"><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="branch-name-input" /></Field>
            <Field label="IFSC"><Input value={form.ifsc} onChange={(e) => set("ifsc", e.target.value)} /></Field>
            <Field label="Branch Type">
              <Select value={form.branch_type} onValueChange={(v) => set("branch_type", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["Retail", "SME", "Rural", "Corporate"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Region"><Input value={form.region} onChange={(e) => set("region", e.target.value)} /></Field>
            <Field label="State"><Input value={form.state} onChange={(e) => set("state", e.target.value)} /></Field>
            <Field label="District"><Input value={form.district} onChange={(e) => set("district", e.target.value)} /></Field>
            <Field label="PIN"><Input value={form.pin} onChange={(e) => set("pin", e.target.value)} /></Field>
            <Field label="Address" className="md:col-span-2"><Input value={form.address} onChange={(e) => set("address", e.target.value)} /></Field>
            <Field label="Contact Phone"><Input value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} /></Field>
            <Field label="Contact Email"><Input value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} /></Field>
            <div className="md:col-span-4 flex justify-end"><Button onClick={add} disabled={busy} data-testid="branch-add-button">Add Branch</Button></div>
          </div>
        </Card>
      )}
      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground"><tr><th className="text-left px-3 py-2.5">Code</th><th className="text-left px-3 py-2.5">Name</th><th className="text-left px-3 py-2.5">IFSC</th><th className="text-left px-3 py-2.5">Region</th><th className="text-left px-3 py-2.5">Type</th><th className="text-left px-3 py-2.5">State</th><th></th></tr></thead>
            <tbody>
              {items.map(b => (
                <tr key={b.id} className="border-t border-border"><td className="px-3 py-2 mono text-xs">{b.code}</td><td className="px-3 py-2 font-medium">{b.name}</td><td className="px-3 py-2 mono text-xs">{b.ifsc || "—"}</td><td className="px-3 py-2 text-xs">{b.region || "—"}</td><td className="px-3 py-2"><Badge variant="secondary" className="text-xs">{b.branch_type}</Badge></td><td className="px-3 py-2 text-xs">{b.state || "—"}</td><td className="px-3 py-2 text-right">{canManage && <Button size="sm" variant="outline" onClick={() => remove(b.id)}><Trash2 className="h-3.5 w-3.5" /></Button>}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function Configs() {
  const { user } = useAuth();
  const canManage = user?.role === "HO";
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ key: "", value: "", category: "policy", scope: "global", description: "", active: true });
  const [busy, setBusy] = useState(false);
  const load = async () => { const r = await api.get("/master/configurations"); setItems(r.data); };
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.key) return toast.error("Key required");
    setBusy(true);
    try {
      const v = isNaN(Number(form.value)) ? form.value : Number(form.value);
      await api.post("/master/configurations", { ...form, value: v });
      toast.success("Config saved");
      setForm({ key: "", value: "", category: "policy", scope: "global", description: "", active: true });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const remove = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/master/configurations/${id}`); toast.success("Deleted"); load(); };
  return (
    <div className="space-y-3 mt-3">
      {canManage && (
        <Card className="p-4">
          <div className="text-sm font-semibold heading mb-3">Add / Update Configuration</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Field label="Key"><Input value={form.key} onChange={(e) => set("key", e.target.value)} placeholder="e.g., min_cibil" data-testid="config-key-input" /></Field>
            <Field label="Value"><Input value={form.value} onChange={(e) => set("value", e.target.value)} data-testid="config-value-input" /></Field>
            <Field label="Category">
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["policy", "scoring", "rates", "limits", "product", "global"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Active"><div className="h-10 flex items-center"><Switch checked={form.active} onCheckedChange={(v) => set("active", v)} /></div></Field>
            <Field label="Description" className="md:col-span-4"><Input value={form.description} onChange={(e) => set("description", e.target.value)} /></Field>
            <div className="md:col-span-4 flex justify-end"><Button onClick={save} disabled={busy} data-testid="config-save-button">Save</Button></div>
          </div>
        </Card>
      )}
      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground"><tr><th className="text-left px-3 py-2.5">Key</th><th className="text-right px-3 py-2.5">Value</th><th className="text-left px-3 py-2.5">Category</th><th className="text-left px-3 py-2.5">Scope</th><th className="text-left px-3 py-2.5">Description</th><th className="text-left px-3 py-2.5">Updated</th><th></th></tr></thead>
            <tbody>
              {items.map(c => (
                <tr key={c.id} className="border-t border-border"><td className="px-3 py-2 mono text-xs">{c.key}</td><td className="px-3 py-2 num-right mono">{typeof c.value === "object" ? JSON.stringify(c.value) : String(c.value)}</td><td className="px-3 py-2"><Badge variant="secondary" className="text-xs">{c.category}</Badge></td><td className="px-3 py-2 text-xs">{c.scope}</td><td className="px-3 py-2 text-xs text-muted-foreground">{c.description}</td><td className="px-3 py-2 text-xs mono text-muted-foreground">{fmtDate(c.updated_at)}</td><td className="px-3 py-2 text-right">{canManage && <Button size="sm" variant="outline" onClick={() => remove(c.id)}><Trash2 className="h-3.5 w-3.5" /></Button>}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
